import React, { useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'

import EmailNotificationSettings from 'components/Settings/Notifications/EmailNotificationSettings.jsx'
import PersonalSettings from 'components/Settings/Personal/PersonalSettings'
import MetaAccess from 'components/Settings/Meta'
import GoogleAccess from 'components/Settings/Google'
import SystemSettings from 'components/Settings/SystemSettings'

import { TransparentBgButton } from 'components/Shared/Button'
import NestedTabPills from 'components/Shared/NestedTabPills'
import useHasPermission, { Permissions } from 'hooks/useHasPermission'

const ItemWrapper = ({
  children,
  cardClassNames = 'card card-default settings-page__card',
  wrapperClassNames = '',
}) => {
  return (
    <div className={`d-flex flex-column w-100 gap-3 mb-5 ${wrapperClassNames}`}>
      <div className="settings-page__container">
        <div className={cardClassNames}>{children}</div>
      </div>
    </div>
  )
}

ItemWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  cardClassNames: PropTypes.string,
}

/**
 * Default page for settings.
 * @Tags( page, settings, layout, logged-users )
 * @SuggestedTags( settings )
 * @Routes( /settings )
 */
const Settings = ({ fetchAndSetProfile }) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const hasGoogleAccess = useHasPermission(Permissions.google)
  const anchor = history.location.hash
  const [currentTab, setCurrentTab] = useState(() => {
    if (anchor === '#notifications') {
      return 1
    }
    if (anchor === '#platforms' || anchor === '#meta') {
      return 2
    }
    if (anchor === '#system') {
      return 3
    }
    if (anchor === '#branding') {
      return 4
    }
    return 0
  })
  const childTab = new URLSearchParams(history.location.search).get('platform') === 'google' ? 1 : 0

  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false)
  // Redirect legacy #meta anchor to #platforms
  useEffect(() => {
    if (history.location.hash === '#meta') {
      history.replace('/settings#platforms')
    }
  }, [history])

  useEffect(() => {
    fetchAndSetProfile(dispatch, i18n)
  }, [dispatch, fetchAndSetProfile, i18n])

  const tabsData = [
    {
      title: t('Settings.PersonalSettings'),
      component: (
        <>
          <ItemWrapper wrapperClassNames="profile-settings-wrapper">
            <PersonalSettings
              setDeleteAccountModalOpen={setDeleteAccountModalOpen}
              deleteAccountModalOpen={deleteAccountModalOpen}
            />
          </ItemWrapper>

          <TransparentBgButton
            color="navy-light"
            onClick={() => setDeleteAccountModalOpen(true)}
            text={t('Settings.deleteAccount.button')}
            classNames="delete-user-profile-button"
          />
        </>
      ),
      anchor: 'personal',
    },
    {
      title: t('Settings.NotificationSettings.title'),
      component: (
        <ItemWrapper>
          <EmailNotificationSettings />
        </ItemWrapper>
      ),
      anchor: 'notifications',
    },
    {
      title: t('Settings.platforms.title'),
      anchor: 'platforms',
      children: [
        { title: t('Settings.platforms.meta'), key: 'meta', component: <MetaAccess /> },
        ...(hasGoogleAccess
          ? [{ title: t('Settings.platforms.google'), key: 'google', component: <GoogleAccess /> }]
          : []),
      ],
    },
    {
      title: t('Settings.system.title'),
      component: (
        <ItemWrapper>
          <SystemSettings />
        </ItemWrapper>
      ),
      anchor: 'system',
    }
  ]

  const handleTabChange = (idx) => {
    setCurrentTab(idx)
    const tab = tabsData[idx]
    history.push(`/settings#${tab.anchor}`)
  }

  const handleChildTabChange = (idx) => {
    const activeTab = tabsData[currentTab]
    const key = activeTab?.children?.[idx]?.key
    const params = new URLSearchParams(history.location.search)
    if (key === 'google') {
      params.set('platform', 'google')
    } else {
      params.delete('platform')
    }
    const qs = params.toString()
    history.push(`/settings${qs ? `?${qs}` : ''}#${activeTab.anchor}`)
  }

  return (
    <Layout
      loggedIn
      title={t('Settings.PageTitle')}
      mobileTopBar={{
        centerTitle: t('Settings.MobileTitle'),
        backToDash: true,
      }}
    >
      <Helmet
        bodyAttributes={{
          class: 'settings-page',
        }}
      />
      <main data-testid="settings-page">
        <div className="container-fluid pb-5">
          <div className="p-3">
            <div>
              <h3 className="main-title hide-mobile">{t('Settings.MainTitle')}</h3>
            </div>
            <NestedTabPills
              tabsData={tabsData}
              currentTab={currentTab}
              handleTabChange={handleTabChange}
              childTab={childTab}
              onChildTabChange={handleChildTabChange}
            />
          </div>
        </div>
      </main>
    </Layout>
  )
}

Settings.propTypes = {
  fetchAndSetProfile: PropTypes.func,
}

export default Settings
